import React, { useState, useMemo } from 'react';
import { Button, Card, StepIndicator, ErrorAlert, FileUpload } from '../../../components/shared';

const STEPS = [
  { key: 'ocr', label: 'Chụp giấy tờ' },
  { key: 'review', label: 'Xác nhận' },
  { key: 'otp', label: 'OTP' },
];

function randDigits(len) { let s = ''; for (let i = 0; i < len; i++) s += Math.floor(Math.random() * 10); return s; }
function findCode(list, keyword) {
  const item = list.find((i) => (i.name || '').toLowerCase().includes(keyword));
  return item ? (item.code || item.value || '') : '';
}

function generateReviewAutoFill(ocrData, catalogs) {
  const { banks = [] } = catalogs || {};
  const fullName = ocrData?.ocr_full_name || 'Nguyen Van A';
  const parts = fullName.split(' ');
  const emailName = `${parts[0]}${parts[parts.length - 1]}${randDigits(3)}`.toLowerCase().replace(/\s/g, '');
  return {
    phoneNumber: '09' + randDigits(8),
    email: `${emailName}@gmail.com`,
    bankId: findCode(banks, 'vietcombank') || findCode(banks, 'vcb') || (banks[0]?.code || ''),
    bankBranch: 'Ho Chi Minh',
    bankAccountNo: randDigits(13),
    bankAccountName: fullName.toUpperCase(),
  };
}

const SPARKLE_ICON = (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const OCR_DISPLAY_FIELDS = [
  { key: 'ocr_full_name', label: 'Họ tên' },
  { key: 'ocr_number_card', label: 'Số CCCD' },
  { key: 'ocr_birthday', label: 'Ngày sinh' },
  { key: 'ocr_gender', label: 'Giới tính' },
  { key: 'ocr_address', label: 'Địa chỉ' },
  { key: 'ocr_issue_date', label: 'Ngày cấp' },
  { key: 'ocr_issue_place', label: 'Nơi cấp' },
  { key: 'ocr_expire_date', label: 'Ngày hết hạn' },
];

function OcrStep({ onSubmit, loading, error }) {
  const [frontCard, setFrontCard] = useState(null);
  const [backCard, setBackCard] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);

  const handleFront = (file) => {
    if (frontPreview) URL.revokeObjectURL(frontPreview);
    setFrontCard(file);
    setFrontPreview(URL.createObjectURL(file));
  };

  const handleBack = (file) => {
    if (backPreview) URL.revokeObjectURL(backPreview);
    setBackCard(file);
    setBackPreview(URL.createObjectURL(file));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (frontCard && backCard) onSubmit(frontCard, backCard);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Chụp giấy tờ tùy thân</h3>
        <p className="mt-1 text-sm text-gray-500">
          Vui lòng chụp mặt trước và mặt sau CMND/CCCD của bạn
        </p>
      </div>

      <div className="space-y-4">
        <FileUpload
          label="Mặt trước CMND/CCCD"
          accept="image/jpeg,image/png"
          preview={frontPreview}
          onChange={handleFront}
          disabled={loading}
        />
        <FileUpload
          label="Mặt sau CMND/CCCD"
          accept="image/jpeg,image/png"
          preview={backPreview}
          onChange={handleBack}
          disabled={loading}
        />
      </div>

      <ErrorAlert error={error} />

      <Button className="w-full" type="submit" loading={loading} disabled={!frontCard || !backCard}>
        Xác minh giấy tờ
      </Button>
    </form>
  );
}

function ReviewStep({ ocrData, catalogs, onSubmit, loading, error }) {
  const { banks = [] } = catalogs || {};

  const [form, setForm] = useState({
    phoneNumber: '',
    email: '',
    bankId: '',
    bankBranch: '',
    bankAccountNo: '',
    bankAccountName: '',
  });

  const update = (field) => (e) => {
    const val = typeof e === 'string' ? e : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const displayData = useMemo(() => {
    if (!ocrData) return [];
    return OCR_DISPLAY_FIELDS
      .filter((f) => ocrData[f.key])
      .map((f) => ({ label: f.label, value: ocrData[f.key] }));
  }, [ocrData]);

  const handleAutoFill = () => {
    const data = generateReviewAutoFill(ocrData, catalogs);
    setForm((f) => ({ ...f, ...data }));
  };

  const canSubmit = form.phoneNumber && form.email;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Xác nhận thông tin</h3>
          <p className="mt-1 text-sm text-gray-500">Kiểm tra thông tin đã nhận dạng và bổ sung thông tin còn thiếu</p>
        </div>
        <button
          type="button"
          onClick={handleAutoFill}
          title="Auto fill"
          className="shrink-0 rounded-lg border border-gray-200 p-2 text-gray-400 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
        >
          {SPARKLE_ICON}
        </button>
      </div>

      {displayData.length > 0 && (
        <div className="rounded-lg bg-gray-50 p-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Thông tin từ giấy tờ</p>
          <div className="space-y-1">
            {displayData.map((d) => (
              <div key={d.label} className="flex justify-between border-b border-gray-100 py-2 last:border-0">
                <span className="text-sm text-gray-500">{d.label}</span>
                <span className="text-sm font-medium text-gray-900">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Thông tin bổ sung</p>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Số điện thoại *</label>
          <input
            type="tel"
            value={form.phoneNumber}
            onChange={update('phoneNumber')}
            placeholder="Nhập số điện thoại"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Email *</label>
          <input
            type="email"
            value={form.email}
            onChange={update('email')}
            placeholder="email@example.com"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Ngân hàng</label>
          <select
            value={form.bankId}
            onChange={update('bankId')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Chọn ngân hàng</option>
            {banks.map((b) => (
              <option key={b.code || b.value} value={b.code || b.value}>{b.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">Số tài khoản</label>
            <input
              type="text"
              value={form.bankAccountNo}
              onChange={update('bankAccountNo')}
              placeholder="Số tài khoản"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">Tên chủ TK</label>
            <input
              type="text"
              value={form.bankAccountName}
              onChange={update('bankAccountName')}
              placeholder="Tên chủ tài khoản"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <ErrorAlert error={error} />

      <Button className="w-full" type="submit" loading={loading} disabled={!canSubmit}>
        Tiếp tục
      </Button>
    </form>
  );
}

function OtpStep({ onSubmit, loading, error }) {
  const [otp, setOtp] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(otp);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
          <svg className="h-7 w-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Nhập mã OTP</h3>
        <p className="mt-1 text-sm text-gray-500">
          Mã OTP đã được gửi đến số điện thoại hoặc email của bạn
        </p>
      </div>

      <div>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="Nhập 6 số OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-center text-2xl font-semibold tracking-widest focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
        />
      </div>

      <ErrorAlert error={error} />

      <Button className="w-full" type="submit" loading={loading} disabled={otp.length < 6}>
        Xác nhận
      </Button>
    </form>
  );
}

const STEP_MAP = { 'VDSC_EKYC_OCR': 0, 'VDSC_EKYC_REVIEW': 1, 'VDSC_REGISTER_OTP': 2 };

export default function VdscRegisterFlow({
  flowState,
  ekycSession,
  catalogs,
  onSubmitOcr,
  onSubmitRegister,
  onSubmitOtp,
  onBack,
  loading,
  error,
}) {
  const currentStep = STEP_MAP[flowState] ?? 0;

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <OcrStep onSubmit={onSubmitOcr} loading={loading} error={error} />;
      case 1:
        return (
          <ReviewStep
            ocrData={ekycSession?.ocrData}
            catalogs={catalogs}
            onSubmit={onSubmitRegister}
            loading={loading}
            error={error}
          />
        );
      case 2:
        return <OtpStep onSubmit={onSubmitOtp} loading={loading} error={error} />;
      default:
        return null;
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Mở tài khoản Rồng Việt</h2>
              <p className="mt-1 text-sm text-gray-500">Xác minh danh tính để mở tài khoản chứng khoán</p>
            </div>
          </div>

          <StepIndicator steps={STEPS} currentStep={currentStep} />

          {renderStep()}

          {currentStep > 0 && (
            <button
              type="button"
              onClick={onBack}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Quay lại bước trước
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}
